import { saveAs } from "file-saver";
import { getPlatformInfo } from "./platformDetection";

const DB_NAME = "cip-local-visit-audio";
const DB_VERSION = 4;
const STORE_NAME = "visitAudioSegments";
const CHUNK_STORE_NAME = "visitAudioChunks";
const DISK_BACKUP_STORE_NAME = "visitAudioDiskBackups";
const DISK_BACKUP_DIRECTORY_STORE_NAME = "visitAudioDiskBackupDirectories";
const RETENTION_MS = 5 * 24 * 60 * 60 * 1000;
const DISK_BACKUP_DIRECTORY_ID = "default";
const DISK_BACKUP_FOLDER_NAME = "Circadia Visit Recording";

export const LOCAL_VISIT_AUDIO_STORED_EVENT = "localVisitAudioStored";

export interface ILocalVisitAudioRecord {
  id: string;
  clinicId: number;
  patientId: number;
  visitNoteId: number;
  sessionId: string;
  segmentId: string;
  segmentNumber: number;
  mimeType: string;
  filename: string;
  size: number;
  createdAt: string;
  expiresAt: string;
  audioBlob: Blob;
}

export interface ISaveLocalVisitAudioRequest {
  clinicId?: number;
  patientId?: number;
  visitNoteId?: number;
  sessionId: string;
  segmentId: string;
  segmentNumber: number;
  audioBlob: Blob;
  mimeType?: string;
  filenameBase?: string;
}

export interface ISaveLocalVisitAudioChunkRequest {
  clinicId?: number;
  patientId?: number;
  visitNoteId?: number;
  sessionId: string;
  segmentId: string;
  segmentNumber: number;
  chunkIndex: number;
  audioBlob: Blob;
  mimeType?: string;
}

interface ILocalVisitAudioChunkRecord {
  id: string;
  parentId: string;
  clinicId: number;
  patientId: number;
  visitNoteId: number;
  sessionId: string;
  segmentId: string;
  segmentNumber: number;
  chunkIndex: number;
  mimeType: string;
  size: number;
  createdAt: string;
  expiresAt: string;
  audioBlob: Blob;
}

interface ILocalVisitAudioDiskBackupRecord {
  visitNoteId: number;
  fileHandle: FileSystemFileHandleLike;
  writePosition: number;
  updatedAt: string;
  expiresAt: string;
}

interface ILocalVisitAudioDiskBackupDirectoryRecord {
  id: string;
  directoryHandle: FileSystemDirectoryHandleLike;
  updatedAt: string;
  expiresAt: string;
}

const canUseIndexedDB = () =>
  typeof window !== "undefined" && typeof window.indexedDB !== "undefined";

// IndexedDB is available in supported mobile browsers and in the CPro
// WKWebView/Android WebView shells as well as desktop browsers. Keep this
// capability check independent from direct filesystem backup support.
export const isLocalVisitAudioSupported = (): boolean => canUseIndexedDB();

const openDatabase = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    if (!isLocalVisitAudioSupported()) {
      reject(new Error("Local visit audio is not supported on this platform"));
      return;
    }

    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("visitNoteId", "visitNoteId", { unique: false });
        store.createIndex("expiresAt", "expiresAt", { unique: false });
      }
      if (!db.objectStoreNames.contains(CHUNK_STORE_NAME)) {
        const store = db.createObjectStore(CHUNK_STORE_NAME, { keyPath: "id" });
        store.createIndex("visitNoteId", "visitNoteId", { unique: false });
        store.createIndex("expiresAt", "expiresAt", { unique: false });
        store.createIndex("parentId", "parentId", { unique: false });
      }
      if (!db.objectStoreNames.contains(DISK_BACKUP_STORE_NAME)) {
        const store = db.createObjectStore(DISK_BACKUP_STORE_NAME, {
          keyPath: "visitNoteId",
        });
        store.createIndex("expiresAt", "expiresAt", { unique: false });
      }
      if (!db.objectStoreNames.contains(DISK_BACKUP_DIRECTORY_STORE_NAME)) {
        const store = db.createObjectStore(DISK_BACKUP_DIRECTORY_STORE_NAME, {
          keyPath: "id",
        });
        store.createIndex("expiresAt", "expiresAt", { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error || new Error("Failed to open local audio store"));
  });

const withStore = async <T>(
  storeName: string,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T> | void
): Promise<T | undefined> => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    const request = run(store);
    let value: T | undefined;

    if (request) {
      request.onsuccess = () => {
        value = request.result;
      };
      request.onerror = () => {
        reject(request.error || new Error("IndexedDB request failed"));
      };
    }

    transaction.oncomplete = () => {
      db.close();
      resolve(value);
    };
    transaction.onerror = () => {
      db.close();
      reject(transaction.error || new Error("IndexedDB transaction failed"));
    };
    transaction.onabort = () => {
      db.close();
      reject(transaction.error || new Error("IndexedDB transaction aborted"));
    };
  });
};

const getAudioFileExtension = (mimeType: string): string => {
  if (mimeType.includes("mp4") || mimeType.includes("m4a")) return "m4a";
  if (mimeType.includes("ogg")) return "ogg";
  if (mimeType.includes("wav")) return "wav";
  if (mimeType.includes("mpeg") || mimeType.includes("mp3")) return "mp3";
  return "webm";
};

const buildRecordId = ({
  visitNoteId,
  sessionId,
  segmentId,
}: {
  visitNoteId: number;
  sessionId: string;
  segmentId: string;
}) => `${visitNoteId}:${sessionId}:${segmentId}`;

const buildChunkId = ({
  visitNoteId,
  sessionId,
  segmentId,
  chunkIndex,
}: {
  visitNoteId: number;
  sessionId: string;
  segmentId: string;
  chunkIndex: number;
}) =>
  `${buildRecordId({ visitNoteId, sessionId, segmentId })}:chunk:${chunkIndex}`;

const sanitizeFilenameBase = (value?: string): string =>
  String(value || "")
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 120);

type FileSystemWritableFileStreamLike = {
  write(
    data: Blob | { type: "seek" | "truncate"; position?: number; size?: number }
  ): Promise<void>;
  close(): Promise<void>;
};

type FileSystemFileHandleLike = {
  getFile(): Promise<File>;
  queryPermission?(options?: { mode?: "read" | "readwrite" }): Promise<string>;
  requestPermission?(options?: {
    mode?: "read" | "readwrite";
  }): Promise<string>;
  createWritable(options?: {
    keepExistingData?: boolean;
  }): Promise<FileSystemWritableFileStreamLike>;
};

type FileSystemDirectoryHandleLike = {
  name: string;
  queryPermission?(options?: { mode?: "read" | "readwrite" }): Promise<string>;
  requestPermission?(options?: {
    mode?: "read" | "readwrite";
  }): Promise<string>;
  getDirectoryHandle(
    name: string,
    options?: { create?: boolean }
  ): Promise<FileSystemDirectoryHandleLike>;
  getFileHandle(
    name: string,
    options?: { create?: boolean }
  ): Promise<FileSystemFileHandleLike>;
};

let diskBackupHandle: FileSystemFileHandleLike | null = null;
let diskBackupWritePosition = 0;
let diskBackupWriteQueue: Promise<void> = Promise.resolve();

export const isLocalVisitAudioDiskBackupSupported = (): boolean => {
  if (typeof window === "undefined") return false;

  const platformInfo = getPlatformInfo();
  return (
    platformInfo.isWeb &&
    platformInfo.isDesktop &&
    (typeof (window as any).showDirectoryPicker === "function" ||
      typeof (window as any).showSaveFilePicker === "function") &&
    isLocalVisitAudioSupported()
  );
};

const ensureDiskBackupPermission = async (
  handle: FileSystemFileHandleLike | FileSystemDirectoryHandleLike
): Promise<boolean> => {
  const permissionOptions = { mode: "readwrite" as const };

  if (typeof handle.queryPermission === "function") {
    const permission = await handle.queryPermission(permissionOptions);
    if (permission === "granted") return true;
  }

  if (typeof handle.requestPermission === "function") {
    const permission = await handle.requestPermission(permissionOptions);
    return permission === "granted";
  }

  return true;
};

const buildDiskBackupFilename = (
  filenameBase?: string,
  visitNoteId?: number | null
): string => {
  const base = sanitizeFilenameBase(filenameBase) || "visit_audio";
  const noteSuffix =
    visitNoteId !== undefined && visitNoteId !== null
      ? `_note_${visitNoteId}`
      : "";
  return `${base}${noteSuffix}.webm`;
};

const getStoredDiskBackup = async (
  visitNoteId?: number | null
): Promise<ILocalVisitAudioDiskBackupRecord | null> => {
  if (!visitNoteId) return null;

  try {
    const record = await withStore<ILocalVisitAudioDiskBackupRecord>(
      DISK_BACKUP_STORE_NAME,
      "readonly",
      (store) => store.get(visitNoteId)
    );

    return record || null;
  } catch (error) {
    console.warn("[LocalVisitAudio] Failed to load disk backup handle:", error);
    return null;
  }
};

const saveStoredDiskBackup = async (
  visitNoteId: number,
  fileHandle: FileSystemFileHandleLike,
  writePosition: number
): Promise<void> => {
  const now = new Date();

  await withStore(DISK_BACKUP_STORE_NAME, "readwrite", (store) =>
    store.put({
      visitNoteId,
      fileHandle,
      writePosition,
      updatedAt: now.toISOString(),
      expiresAt: new Date(now.getTime() + RETENTION_MS).toISOString(),
    })
  );
};

const getStoredDiskBackupDirectory =
  async (): Promise<ILocalVisitAudioDiskBackupDirectoryRecord | null> => {
    try {
      const record = await withStore<ILocalVisitAudioDiskBackupDirectoryRecord>(
        DISK_BACKUP_DIRECTORY_STORE_NAME,
        "readonly",
        (store) => store.get(DISK_BACKUP_DIRECTORY_ID)
      );

      return record || null;
    } catch (error) {
      console.warn(
        "[LocalVisitAudio] Failed to load disk backup directory:",
        error
      );
      return null;
    }
  };

const saveStoredDiskBackupDirectory = async (
  directoryHandle: FileSystemDirectoryHandleLike
): Promise<void> => {
  const now = new Date();

  await withStore(DISK_BACKUP_DIRECTORY_STORE_NAME, "readwrite", (store) =>
    store.put({
      id: DISK_BACKUP_DIRECTORY_ID,
      directoryHandle,
      updatedAt: now.toISOString(),
      expiresAt: new Date(now.getTime() + RETENTION_MS).toISOString(),
    })
  );
};

const requestDiskBackupParentDirectory =
  async (): Promise<FileSystemDirectoryHandleLike> => {
    const showDirectoryPicker = (window as any).showDirectoryPicker;

    try {
      return await showDirectoryPicker({
        id: "circadia-visit-recording",
        mode: "readwrite",
        startIn: "desktop",
      });
    } catch (error: any) {
      if (error?.name === "TypeError") {
        return showDirectoryPicker({
          id: "circadia-visit-recording",
          mode: "readwrite",
        });
      }
      throw error;
    }
  };

const getOrRequestDiskBackupDirectory =
  async (): Promise<FileSystemDirectoryHandleLike | null> => {
    const storedDirectory = await getStoredDiskBackupDirectory();

    if (
      storedDirectory &&
      (await ensureDiskBackupPermission(storedDirectory.directoryHandle))
    ) {
      return storedDirectory.directoryHandle;
    }

    if (typeof (window as any).showDirectoryPicker !== "function") {
      return null;
    }

    const parentDirectory = await requestDiskBackupParentDirectory();
    if (!(await ensureDiskBackupPermission(parentDirectory))) return null;

    const recordingsDirectory =
      parentDirectory.name === DISK_BACKUP_FOLDER_NAME
        ? parentDirectory
        : await parentDirectory.getDirectoryHandle(DISK_BACKUP_FOLDER_NAME, {
            create: true,
          });

    if (!(await ensureDiskBackupPermission(recordingsDirectory))) return null;

    await saveStoredDiskBackupDirectory(recordingsDirectory);

    return recordingsDirectory;
  };

export const prepareLocalVisitAudioDiskBackup = async (
  filenameBase?: string,
  visitNoteId?: number | null
): Promise<boolean> => {
  if (!isLocalVisitAudioDiskBackupSupported()) return false;

  try {
    const existingBackup = await getStoredDiskBackup(visitNoteId);
    if (
      existingBackup &&
      (await ensureDiskBackupPermission(existingBackup.fileHandle))
    ) {
      try {
        const file = await existingBackup.fileHandle.getFile();

        diskBackupHandle = existingBackup.fileHandle;
        diskBackupWritePosition = file.size;
        diskBackupWriteQueue = Promise.resolve();

        await saveStoredDiskBackup(
          existingBackup.visitNoteId,
          existingBackup.fileHandle,
          file.size
        );

        return true;
      } catch (error) {
        console.warn(
          "[LocalVisitAudio] Stored disk backup handle is unavailable:",
          error
        );
      }
    }

    const recordingsDirectory = await getOrRequestDiskBackupDirectory();

    if (recordingsDirectory) {
      const handle = await recordingsDirectory.getFileHandle(
        buildDiskBackupFilename(filenameBase, visitNoteId),
        { create: true }
      );
      const file = await handle.getFile();

      diskBackupHandle = handle;
      diskBackupWritePosition = file.size;
      diskBackupWriteQueue = Promise.resolve();

      if (visitNoteId) {
        await saveStoredDiskBackup(visitNoteId, handle, file.size);
      }

      return true;
    }

    const base = buildDiskBackupFilename(filenameBase, visitNoteId).replace(
      /\.webm$/i,
      ""
    );
    const handle = await (window as any).showSaveFilePicker({
      suggestedName: `${base}.webm`,
      types: [
        {
          description: "WebM audio",
          accept: { "audio/webm": [".webm"] },
        },
      ],
    });

    diskBackupHandle = handle;
    diskBackupWritePosition = 0;
    diskBackupWriteQueue = Promise.resolve();

    if (visitNoteId) {
      await saveStoredDiskBackup(visitNoteId, handle, 0);
    }

    return true;
  } catch (error: any) {
    if (error?.name !== "AbortError") {
      console.warn("[LocalVisitAudio] Failed to prepare disk backup:", error);
    }
    diskBackupHandle = null;
    diskBackupWritePosition = 0;
    diskBackupWriteQueue = Promise.resolve();
    return false;
  }
};

export const persistCurrentLocalVisitAudioDiskBackup = async (
  visitNoteId?: number | null
): Promise<void> => {
  if (!visitNoteId || !diskBackupHandle) return;

  await saveStoredDiskBackup(
    visitNoteId,
    diskBackupHandle,
    diskBackupWritePosition
  );
};

export const appendLocalVisitAudioDiskBackupChunk = async (
  audioBlob: Blob
): Promise<void> => {
  if (!diskBackupHandle || audioBlob.size === 0) return;

  diskBackupWriteQueue = diskBackupWriteQueue
    .catch(() => undefined)
    .then(async () => {
      if (!diskBackupHandle) return;

      const writable = await diskBackupHandle.createWritable({
        keepExistingData: true,
      });
      await writable.write({ type: "seek", position: diskBackupWritePosition });
      await writable.write(audioBlob);
      diskBackupWritePosition += audioBlob.size;
      await writable.write({ type: "truncate", size: diskBackupWritePosition });
      await writable.close();
    })
    .catch((error) => {
      console.warn(
        "[LocalVisitAudio] Failed to append disk backup chunk:",
        error
      );
      diskBackupHandle = null;
      diskBackupWritePosition = 0;
    });

  await diskBackupWriteQueue;
};

export const finishLocalVisitAudioDiskBackup = (): void => {
  const handleToClear = diskBackupHandle;
  diskBackupWriteQueue.finally(() => {
    if (diskBackupHandle !== handleToClear) return;

    diskBackupHandle = null;
    diskBackupWritePosition = 0;
    diskBackupWriteQueue = Promise.resolve();
  });
};

export const cleanupExpiredLocalVisitAudio = async (): Promise<void> => {
  if (!isLocalVisitAudioSupported()) return;

  try {
    const now = new Date().toISOString();
    await Promise.all(
      [
        STORE_NAME,
        CHUNK_STORE_NAME,
        DISK_BACKUP_STORE_NAME,
        DISK_BACKUP_DIRECTORY_STORE_NAME,
      ].map((storeName) =>
        withStore(storeName, "readwrite", (store) => {
          const index = store.index("expiresAt");
          const range = IDBKeyRange.upperBound(now);
          const cursorRequest = index.openCursor(range);

          cursorRequest.onsuccess = () => {
            const cursor = cursorRequest.result;
            if (!cursor) return;

            cursor.delete();
            cursor.continue();
          };
        })
      )
    );
  } catch (error) {
    console.warn("[LocalVisitAudio] Failed to clean up expired audio", error);
  }
};

const deleteLocalVisitAudioChunksForParent = async (
  parentId: string
): Promise<void> => {
  await withStore(CHUNK_STORE_NAME, "readwrite", (store) => {
    const cursorRequest = store
      .index("parentId")
      .openCursor(IDBKeyRange.only(parentId));

    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result;
      if (!cursor) return;

      cursor.delete();
      cursor.continue();
    };
  });
};

export const saveLocalVisitAudio = async ({
  clinicId,
  patientId,
  visitNoteId,
  sessionId,
  segmentId,
  segmentNumber,
  audioBlob,
  mimeType,
  filenameBase,
}: ISaveLocalVisitAudioRequest): Promise<ILocalVisitAudioRecord | null> => {
  if (
    !canUseIndexedDB() ||
    !isLocalVisitAudioSupported() ||
    !clinicId ||
    !patientId ||
    !visitNoteId ||
    !audioBlob ||
    audioBlob.size === 0
  ) {
    return null;
  }

  await cleanupExpiredLocalVisitAudio();

  const createdAt = new Date();
  const resolvedMimeType = (mimeType || audioBlob.type || "audio/webm").split(
    ";"
  )[0];
  const extension = getAudioFileExtension(resolvedMimeType);
  const resolvedFilenameBase =
    sanitizeFilenameBase(filenameBase) || `visit_${visitNoteId}`;
  const record: ILocalVisitAudioRecord = {
    id: buildRecordId({ visitNoteId, sessionId, segmentId }),
    clinicId,
    patientId,
    visitNoteId,
    sessionId,
    segmentId,
    segmentNumber,
    mimeType: resolvedMimeType,
    filename: `${resolvedFilenameBase}_segment_${segmentNumber}.${extension}`,
    size: audioBlob.size,
    createdAt: createdAt.toISOString(),
    expiresAt: new Date(createdAt.getTime() + RETENTION_MS).toISOString(),
    audioBlob:
      audioBlob.type === resolvedMimeType
        ? audioBlob
        : new Blob([audioBlob], { type: resolvedMimeType }),
  };

  await withStore(STORE_NAME, "readwrite", (store) => store.put(record));
  await deleteLocalVisitAudioChunksForParent(record.id);

  window.dispatchEvent(
    new CustomEvent(LOCAL_VISIT_AUDIO_STORED_EVENT, {
      detail: {
        visitNoteId,
        recordId: record.id,
      },
    })
  );

  return record;
};

export const saveLocalVisitAudioChunk = async ({
  clinicId,
  patientId,
  visitNoteId,
  sessionId,
  segmentId,
  segmentNumber,
  chunkIndex,
  audioBlob,
  mimeType,
}: ISaveLocalVisitAudioChunkRequest): Promise<ILocalVisitAudioChunkRecord | null> => {
  if (
    !canUseIndexedDB() ||
    !isLocalVisitAudioSupported() ||
    !clinicId ||
    !patientId ||
    !visitNoteId ||
    !audioBlob ||
    audioBlob.size === 0
  ) {
    return null;
  }

  await cleanupExpiredLocalVisitAudio();

  const createdAt = new Date();
  const resolvedMimeType = (mimeType || audioBlob.type || "audio/webm").split(
    ";"
  )[0];
  const parentId = buildRecordId({ visitNoteId, sessionId, segmentId });
  const record: ILocalVisitAudioChunkRecord = {
    id: buildChunkId({ visitNoteId, sessionId, segmentId, chunkIndex }),
    parentId,
    clinicId,
    patientId,
    visitNoteId,
    sessionId,
    segmentId,
    segmentNumber,
    chunkIndex,
    mimeType: resolvedMimeType,
    size: audioBlob.size,
    createdAt: createdAt.toISOString(),
    expiresAt: new Date(createdAt.getTime() + RETENTION_MS).toISOString(),
    audioBlob:
      audioBlob.type === resolvedMimeType
        ? audioBlob
        : new Blob([audioBlob], { type: resolvedMimeType }),
  };

  await withStore(CHUNK_STORE_NAME, "readwrite", (store) => store.put(record));

  // A final segment can finish saving while an asynchronous chunk write is
  // still in flight. Remove this late chunk if its completed record exists.
  const completedRecord = await withStore<ILocalVisitAudioRecord>(
    STORE_NAME,
    "readonly",
    (store) => store.get(record.parentId)
  );
  if (completedRecord) {
    await withStore(CHUNK_STORE_NAME, "readwrite", (store) =>
      store.delete(record.id)
    );
    return record;
  }

  window.dispatchEvent(
    new CustomEvent(LOCAL_VISIT_AUDIO_STORED_EVENT, {
      detail: {
        visitNoteId,
        recordId: record.parentId,
      },
    })
  );

  return record;
};

export const listLocalVisitAudioForNote = async (
  visitNoteId: number
): Promise<ILocalVisitAudioRecord[]> => {
  if (!isLocalVisitAudioSupported()) return [];

  await cleanupExpiredLocalVisitAudio();

  const records = await withStore<ILocalVisitAudioRecord[]>(
    STORE_NAME,
    "readonly",
    (store) => store.index("visitNoteId").getAll(visitNoteId)
  );

  const completedRecords = records || [];
  const completedRecordIds = new Set(
    completedRecords.map((record) => record.id)
  );
  const chunkRecords =
    (await withStore<ILocalVisitAudioChunkRecord[]>(
      CHUNK_STORE_NAME,
      "readonly",
      (store) => store.index("visitNoteId").getAll(visitNoteId)
    )) || [];
  const chunksByParentId = chunkRecords.reduce<
    Record<string, ILocalVisitAudioChunkRecord[]>
  >((acc, record) => {
    if (completedRecordIds.has(record.parentId)) return acc;
    acc[record.parentId] ??= [];
    acc[record.parentId].push(record);
    return acc;
  }, {});
  const recoveredRecords = Object.values(chunksByParentId).flatMap((chunks) => {
    const sortedChunks = chunks.sort((a, b) => a.chunkIndex - b.chunkIndex);
    const firstChunk = sortedChunks[0];
    if (!firstChunk || firstChunk.chunkIndex !== 0) return [];

    const audioBlob = new Blob(
      sortedChunks.map((chunk) => chunk.audioBlob),
      { type: firstChunk.mimeType }
    );
    const extension = getAudioFileExtension(firstChunk.mimeType);
    const recoveredRecord: ILocalVisitAudioRecord = {
      id: `${firstChunk.parentId}:recovered`,
      clinicId: firstChunk.clinicId,
      patientId: firstChunk.patientId,
      visitNoteId: firstChunk.visitNoteId,
      sessionId: firstChunk.sessionId,
      segmentId: firstChunk.segmentId,
      segmentNumber: firstChunk.segmentNumber,
      mimeType: firstChunk.mimeType,
      filename: `visit_${firstChunk.visitNoteId}_segment_${firstChunk.segmentNumber}_recovered.${extension}`,
      size: audioBlob.size,
      createdAt: firstChunk.createdAt,
      expiresAt: sortedChunks[sortedChunks.length - 1].expiresAt,
      audioBlob,
    };

    return [recoveredRecord];
  });

  return [...completedRecords, ...recoveredRecords].sort(
    (a, b) =>
      a.segmentNumber - b.segmentNumber ||
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
};

export const downloadLocalVisitAudioRecord = (
  record: ILocalVisitAudioRecord,
  filenameBase?: string,
  includeSegmentSuffix = false
) => {
  const resolvedFilenameBase = sanitizeFilenameBase(filenameBase);
  const extension = getAudioFileExtension(record.mimeType);
  const filename =
    resolvedFilenameBase && includeSegmentSuffix
      ? `${resolvedFilenameBase}_segment_${record.segmentNumber}.${extension}`
      : resolvedFilenameBase
      ? `${resolvedFilenameBase}.${extension}`
      : record.filename;

  saveAs(record.audioBlob, filename);
};

const getCombinedFilename = (
  records: ILocalVisitAudioRecord[],
  filenameBase?: string
): string => {
  const resolvedFilenameBase = sanitizeFilenameBase(filenameBase);
  if (resolvedFilenameBase) return `${resolvedFilenameBase}.wav`;

  const firstFilename = records[0]?.filename || "visit_audio.wav";
  const withoutSegment = firstFilename.replace(/_segment_\d+\.[^.]+$/i, "");
  const withoutExtension = firstFilename.replace(/\.[^.]+$/i, "");

  return `${withoutSegment || withoutExtension || "visit_audio"}.wav`;
};

const getAudioContext = (): AudioContext => {
  const AudioContextCtor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;

  if (!AudioContextCtor) {
    throw new Error("AudioContext is not available");
  }

  return new AudioContextCtor();
};

const combineAudioBuffers = (
  audioContext: AudioContext,
  buffers: AudioBuffer[]
): AudioBuffer => {
  const sampleRate = buffers[0]?.sampleRate || audioContext.sampleRate;
  const numberOfChannels = Math.max(
    1,
    ...buffers.map((buffer) => buffer.numberOfChannels)
  );
  const totalLength = buffers.reduce((sum, buffer) => sum + buffer.length, 0);
  const combined = audioContext.createBuffer(
    numberOfChannels,
    totalLength,
    sampleRate
  );

  let offset = 0;
  buffers.forEach((buffer) => {
    for (let channel = 0; channel < numberOfChannels; channel += 1) {
      const sourceChannel =
        channel < buffer.numberOfChannels
          ? buffer.getChannelData(channel)
          : buffer.getChannelData(0);
      combined.copyToChannel(sourceChannel, channel, offset);
    }
    offset += buffer.length;
  });

  return combined;
};

const audioBufferToWavBlob = (buffer: AudioBuffer): Blob => {
  const numberOfChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const bytesPerSample = 2;
  const blockAlign = numberOfChannels * bytesPerSample;
  const dataSize = buffer.length * blockAlign;
  const wavSize = 44 + dataSize;
  const arrayBuffer = new ArrayBuffer(wavSize);
  const view = new DataView(arrayBuffer);
  let offset = 0;

  const writeString = (value: string) => {
    for (let i = 0; i < value.length; i += 1) {
      view.setUint8(offset, value.charCodeAt(i));
      offset += 1;
    }
  };

  writeString("RIFF");
  view.setUint32(offset, wavSize - 8, true);
  offset += 4;
  writeString("WAVE");
  writeString("fmt ");
  view.setUint32(offset, 16, true);
  offset += 4;
  view.setUint16(offset, 1, true);
  offset += 2;
  view.setUint16(offset, numberOfChannels, true);
  offset += 2;
  view.setUint32(offset, sampleRate, true);
  offset += 4;
  view.setUint32(offset, sampleRate * blockAlign, true);
  offset += 4;
  view.setUint16(offset, blockAlign, true);
  offset += 2;
  view.setUint16(offset, bytesPerSample * 8, true);
  offset += 2;
  writeString("data");
  view.setUint32(offset, dataSize, true);
  offset += 4;

  for (let frame = 0; frame < buffer.length; frame += 1) {
    for (let channel = 0; channel < numberOfChannels; channel += 1) {
      const sample = Math.max(
        -1,
        Math.min(1, buffer.getChannelData(channel)[frame] || 0)
      );
      view.setInt16(
        offset,
        sample < 0 ? sample * 0x8000 : sample * 0x7fff,
        true
      );
      offset += bytesPerSample;
    }
  }

  return new Blob([arrayBuffer], { type: "audio/wav" });
};

export const downloadLocalVisitAudioRecords = async (
  records: ILocalVisitAudioRecord[],
  filenameBase?: string
): Promise<{ combined: boolean; downloadCount: number }> => {
  if (records.length === 0) {
    return { combined: false, downloadCount: 0 };
  }

  if (records.length === 1) {
    downloadLocalVisitAudioRecord(records[0], filenameBase);
    return { combined: false, downloadCount: 1 };
  }

  let audioContext: AudioContext | null = null;

  try {
    audioContext = getAudioContext();
    const decodedBuffers = await Promise.all(
      records.map(async (record) =>
        audioContext!.decodeAudioData(await record.audioBlob.arrayBuffer())
      )
    );
    const combinedBuffer = combineAudioBuffers(audioContext, decodedBuffers);
    const combinedBlob = audioBufferToWavBlob(combinedBuffer);

    saveAs(combinedBlob, getCombinedFilename(records, filenameBase));

    return { combined: true, downloadCount: 1 };
  } catch (error) {
    console.warn(
      "[LocalVisitAudio] Failed to combine audio segments; downloading separately",
      error
    );
    records.forEach((record) =>
      downloadLocalVisitAudioRecord(record, filenameBase, records.length > 1)
    );
    return { combined: false, downloadCount: records.length };
  } finally {
    if (audioContext) {
      void audioContext.close();
    }
  }
};
